import { useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";
import { Button, Form, Modal, Row } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import SubmitButton from "../components/SubmitButton";
import {
  useDeleteCashSetting,
  useQueryCashSettingAssignmentCounts,
} from "./lib";

const CashSettingDeleteDialog = ({
  showModal,
  setShowModal,
  deleteCashSettingId,
  setDeleteCashSettingId,
  selectedCashSettingId,
  setCashSettingId,
  cashSettingOptions,
  cashSettingsLoaded,
  title,
}: {
  showModal: boolean;
  setShowModal: React.Dispatch<React.SetStateAction<boolean>>;
  deleteCashSettingId: number | null;
  setDeleteCashSettingId: React.Dispatch<React.SetStateAction<number | null>>;
  selectedCashSettingId: number | null;
  setCashSettingId: React.Dispatch<React.SetStateAction<number | null>>;
  cashSettingOptions: JSX.Element[];
  // false while the cash setting walk behind `cashSettingOptions` runs or
  // failed; the options then hold only the "Loading..." or the error text
  cashSettingsLoaded: boolean;
  title: string;
}) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const {
    isLoading: isLoadingAssignmentCount,
    isError: isErrorAssignmentCount = false,
    data: assignmentCount,
  } = useQueryCashSettingAssignmentCounts(deleteCashSettingId ?? -1, {
    enabled: deleteCashSettingId !== null,
  });

  // APP-1970: App disabled nothing here, so a failed count read showed the
  // spec R36 text and "Delete" cleared the cash setting of every account, and
  // a "Delete" while the walk ran or failed sent the "None" replacement.
  // "Delete" is now disabled, never hidden, when the count read failed (spec
  // R49, with the R71 text as the reason), or when the count is above 0 and
  // the walk is not loaded (spec R66; the option text of the select is the
  // reason). Go copies this behavior.
  const isDeleteDisabled =
    isErrorAssignmentCount ||
    ((assignmentCount ?? 0) > 0 && !cashSettingsLoaded);

  const handleModalClose = useCallback(() => {
    setCashSettingId(null);
    setShowModal(false);
  }, [setCashSettingId, setShowModal]);

  // moves the cash setting's accounts to the selected cash setting and
  // deletes the cash setting, in one request
  const deleteCashSetting = useDeleteCashSetting(
    deleteCashSettingId ?? -1,
    selectedCashSettingId,
  );

  const handleSaveChanges = useCallback(async () => {
    if (isDeleteDisabled) {
      return;
    }
    if (deleteCashSettingId !== null) {
      await deleteCashSetting.mutateAsync();
      // `useQueryCashSettings` walks the list again when the delete succeeds
      // (APP-1651). The assignment counts are keyed under `/v1` (APP-1651);
      // the delete moved the accounts, so the replacement's count changed too.
      queryClient.invalidateQueries({
        queryKey: ["v1", "cash-settings"],
      });
      setShowModal(false);
      setCashSettingId(null);
      navigate("/cash/settings");
    }
  }, [
    isDeleteDisabled,
    deleteCashSettingId,
    navigate,
    queryClient,
    deleteCashSetting,
    setCashSettingId,
    setShowModal,
  ]);

  return (
    <Modal
      show={showModal && !isLoadingAssignmentCount}
      onHide={handleModalClose}
      aria-labelledby="contained-modal-title-vcenter"
      scrollable={true}
      centered
    >
      <Modal.Header>
        <Modal.Title>Delete Cash Setting</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {isErrorAssignmentCount ? (
          <Row className="m-2">
            The number of accounts of this cash setting did not load. Close this
            dialog and try again.
          </Row>
        ) : (assignmentCount ?? 0) === 0 ? (
          <Row className="m-2">
            Are you sure you would like to delete this cash setting?
          </Row>
        ) : (
          <>
            <Row className="m-2">
              This cash setting is assigned to {assignmentCount ?? 0}{" "}
              account(s). Which cash setting would you instead like to assign to
              these account(s)?
            </Row>
            <Row className="m-2">
              <Form.Select
                placeholder="Select assigned cash setting"
                value={selectedCashSettingId ?? undefined}
                onChange={(e) =>
                  setCashSettingId(
                    e.target.value === "" ? null : +e.target.value,
                  )
                }
              >
                {cashSettingOptions}
              </Form.Select>
            </Row>
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleModalClose}>
          Discard
        </Button>
        <SubmitButton
          variant="danger"
          label="Delete"
          disabled={isDeleteDisabled}
          onClick={() => handleSaveChanges()}
        />
      </Modal.Footer>
    </Modal>
  );
};
export default CashSettingDeleteDialog;
